import {
  helpers,
  maxLength as vMaxLength,
  required as vRequired,
  requiredIf as vRequiredIf
} from '@vuelidate/validators';
import { isAfter, isBefore, isEqual } from 'date-fns';
import { asDate } from './helpers/dateHelpers';

export const filesHaveNoErrors = {
  /** @type {(value: Array<{error: any}>) => boolean} */
  $validator: value => !value.some(f => f.error),
  $message: 'Some files could not be uploaded'
};

/**
 * Creates a validator that validates that the cumulative size of all file attachments
 * does not exceed a specified amount, in MB.
 * @param {Number} size The maximum allowed size of all files combined.
 * @returns A validator that will test the combined size of all files.
 */
export const filesMaxTotalSize = size => ({
  /** @type {(value: Array<{size: Number}>) => boolean} */
  $validator: value =>
    value.map(f => f.size).reduce((a, c) => a + c, 0) <= (size * 1048576),
  $message: `Total size of files cannot exceed ${size}MB`
});

/**
 * Creates a validator that checks whether a value is on or after the given
 * target date.
 * @param {Date|String|Function} targetLabel The label of the target date to which to compare the
 *  value.
 * @param {Date|String|Function} targetVal The value of the target date to which to compare the
 *  value.
 * @returns A validator that will test values against the target date.
 */
export const isOnOrAfterOther = (targetLabel, targetVal) => ({
  /** @type {(value: Date|String) => boolean} */
  $validator(value, parent) {
    if (!value || !targetVal) return true;

    const valueAsDate = asDate(value);
    const targetAsDate = asDate(
      typeof targetVal === 'function' ? targetVal.call(this, value, parent) : helpers.unwrap(targetVal)
    );

    return isEqual(valueAsDate, targetAsDate) || isAfter(valueAsDate, targetAsDate);
  },
  $message: `must be on or after ${targetLabel}`
});

export const isAfterToday = {
  /** @type {(value: Date|String) => boolean} */
  $validator: (value) => {
    if (!value) return true;
    const valueAsDate = asDate(value);
    return isAfter(valueAsDate, new Date());
  },
  $message: 'must be after today'
};

export const isBeforeToday = {
  /** @type {(value: Date|String) => boolean} */
  $validator: (value) => {
    if (!value) return true;
    const valueAsDate = asDate(value);
    return isBefore(valueAsDate, new Date());
  },
  $message: 'must be before today'
};

/**
 * Creates a validator that checks whether a value is exactly a certain length.
 * @param {Numer} num The exact length that the value should be.
 * @returns A validator that will test values for the given length.
 */
export const length = num => ({
  /** @type {(value: String) => boolean} */
  $validator: value => !value || value?.length === num,
  $message: `must be exactly ${num} characters`
});

export const maxLength = args => helpers.withMessage(
  ({ $params }) => `must be no more than ${$params.max} characters`,
  vMaxLength(args)
);

/**
 * Creates a validator that checks values against a regular expression.
 * @param {RegExp} pattern A regular expression that the value must match.
 * @returns A validator that will test values for the given pattern.
 */
export const regex = pattern => ({
  /** @type {(value: String) => boolean} */
  $validator: value => !value || pattern.test(value),
  $message: 'must have a valid format'
});

export const email = regex(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/);

export const phone = regex(/^\(\d{3}\) \d{3}-\d{4}$/);

export const required = helpers.withMessage(
  'is required',
  vRequired
);

export const requiredIf = args => helpers.withMessage(
  'is required',
  vRequiredIf(args)
);
